// @flow
import type { User } from '~/app/types';
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Button from '~/app/components/Button';
import { flashMessage as __flashMessage } from '~/app/actions';
import injectSheet from 'react-jss';
import { gql, useQuery, useMutation } from '@apollo/client';
import { useSelector, useDispatch } from 'react-redux';
import _ from 'shared/lib/gettext';
import RolesPanel from './RolePanels';

type Props = {
  user: User,
  isFetching: boolean,
  categories: Object,
  update: (id: string, user: User) => Promise<*>,
  flashMessage: Function
}

const EditRole = (props: Props) => {
  const { classes: c } = props;

  const [draft, setDraft] = useState({});

  const dispatch = useDispatch();
  const flashMessage = (message, type) => dispatch(__flashMessage(message, type));

  const { id } = useParams();

  // getUser
  const { data, loading: isFetching } = useQuery(QUERY, {
    variables: { id },
    fetchPolicy: 'network-only'
  });
  const user = data?.user;

  // updateUser
  const [__updateUser] = useMutation(ACTIONS, {
    refetchQueries: ['getUser', 'getUsers']
  });
  const updateUser = (id, user) => __updateUser({
    variables: { id, user }
  });

  // Get current user info from redux
  const {
    user: currentUser,
  } = useSelector(mapStateToProps);


  useEffect(() => {
    // Only fill draft for the first time
    if (Object.keys(draft).length === 0 && user) {
      const roles = user.roles;

      // Keeps only attributes that are needed for graphql.
      const categories = roles.categories.map(
        c => ({ category: c.category, roleType: c.roleType })
      );
      const others = roles.others.map(
        c => ({ category: c.category, roleType: c.roleType })
      );

      setDraft({
        ...user,
        roles: {
          categories,
          others
        }
      });
    }
  }, [draft, user]);

  const onUpdateRolesPanel = (roles) => {
    setDraft({
      ...draft,
      roles
    });
  }

  const handleClickAdmin = () => {
    const { isSuperAdmin } = draft;

    setDraft({
      ...draft,
      isSuperAdmin: !isSuperAdmin
    });
  }

  const handleClickActive = () => {
    const { isActive } = draft;

    setDraft({
      ...draft,
      isActive: !isActive
    });
  }

  /**
   * Save the edited roles to the database.
   */
  const handleSaveRole = async () => {
    const {
      username,
      email,
      createdBy,
      updatedBy,
      isSuperAdmin,
      isActive,
      roles
    } = draft;

    await updateUser(id, {
      _id: id,
      username,
      email,
      createdBy,
      updatedBy,
      isSuperAdmin,
      isActive,
      roles
    });

    // User is modifying their own permissions - reload page.
    if (currentUser._id === id) {
      location.reload();
    } else {
      flashMessage('Successfully saved user roles!');
    }
  }

  if (isFetching) return <span>Loading...</span>;

  const { username, email, isSuperAdmin, roles, isActive } = draft;

  return (
    <div className={c.container}>
      <div className={c.title}>{_('ROLE_MANAGEMENT')}</div>
      <div className={c.row}>
        <div className={c.labelContainer}>{_('USERNAME')}</div>
        <div className={c.flexFive}>{username}</div>
      </div>
      <div className={c.row}>
        <div className={c.labelContainer}>{_('EMAIL')}</div>
        <div className={c.flexFive}>{email}</div>
      </div>
      <div className={c.row}>
        <div className={c.labelContainer}>{_('ADMIN')}</div>
        <div className={c.flexFive}>
          <input
            type="checkbox"
            checked={!!isSuperAdmin}
            onChange={handleClickAdmin}
          />
        </div>
      </div>
      <div className={c.row}>
        <div className={c.labelContainer}>{_('ACTIVE')}</div>
        <div className={c.flexFive}>
          <input
            type="checkbox"
            checked={!!isActive}
            onChange={handleClickActive}
          />
        </div>
      </div>
      <div className={c.row}>
        <div className={c.labelContainer}>{_('ROLES')}</div>
        <div className={c.flexFive}>
          {roles &&
            <RolesPanel roles={roles} onUpdate={onUpdateRolesPanel} />}
        </div>
      </div>
      <div className={c.row}>
        <div className={c.labelContainer} />
        <div className={c.flexFive}>
          <Button primary onClick={handleSaveRole}>{_('SAVE')}</Button>
        </div>
      </div>
    </div>
  );
}

/* =============================================================================
=    Redux
============================================================================= */
const mapStateToProps = (state) => ({
  user: state.app.user,
});

/* =============================================================================
=    GraphQL
============================================================================= */
const QUERY = gql`
  query getUser($id: ID!) {
    user (id: $id) {
      _id
      username
      email
      createdBy
      createdAt
      updatedBy
      updatedAt
      isSuperAdmin
      isActive
      roles {
        categories {
          category
          roleType
        }
        others {
          category
          roleType
        }
      }
    }
  }
`;

const ACTIONS = gql`
  mutation updateUser($id: String!, $user: UserInput) {
    updateUser(_id: $id, user: $user) {
      _id
      username
      email
      createdBy
      createdAt
      updatedBy
      updatedAt
      isSuperAdmin
      isActive
      roles {
        categories {
          category
          roleType
        }
        others {
          category
          roleType
        }
      }
    }
  }
`;

/* =============================================================================
=    CSS
============================================================================= */
const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    padding: '20px 30px',
    fontSize: '12px'
  },
  title: {
    fontSize: '1rem'
  },
  row: {
    display: 'flex',
    padding: '5px 0'
  },
  labelContainer: {
    flex: 1,
    textAlign: 'right',
    paddingRight: '15px',
    fontWeight: 'bold'
  },
  flexFive: {
    flex: 5
  }
};

export default injectSheet(styles)(EditRole);
